import { FC, useMemo } from 'react';
import Container from 'src/components/atoms/Container';
import { DataTable } from 'src/components/organisms/DataTable/DataTable';
import { TableWrapper } from 'src/components/styled/TableWrapper';
import { SalesCampaignOverviewRow } from 'src/domain/table/sales-campaign-overview.row';
import { useStyleCategoriesQuery } from 'src/hooks/style-categories/queries/useStyleCategoriesQuery';
import { useVat } from 'src/hooks/turnover/useVat';
import { calculateGrossMarginPercent } from 'src/utils/gross-margin/calculateGrossMargin';
import { calculateSummary } from '../calculateSummary';
import { categorySalesSummaryColumns, createTotalBoldRowStyle, defaultColDef } from '../data/columns';
import { SummaryReportProps } from '../types';

export const CategorySalesSummaryTab: FC<SummaryReportProps> = ({ salesCampaignRows }) => {
    const { data: styleCategories } = useStyleCategoriesQuery();
    const vat = useVat();

    const byCategory = useMemo(() => {
        return salesCampaignRows.reduce((acc, row) => {
            if (!row.styleCategoryId) {
                return acc;
            }

            const currentRows = acc.get(row.styleCategoryId) ?? [];
            currentRows.push(row);
            acc.set(row.styleCategoryId, currentRows);

            return acc;
        }, new Map<number, SalesCampaignOverviewRow[]>());
    }, [salesCampaignRows]);

    const summaryRows = useMemo(() => {
        if (!styleCategories) {
            return null;
        }

        const rows = styleCategories
            .toSorted((a, b) => (a.order ?? Infinity) - (b.order ?? Infinity))
            .map((category) => {
                const rows = byCategory.get(category.id) ?? [];

                const summaryData = calculateSummary(rows);
                const grossMarginPercent = calculateGrossMarginPercent(
                    summaryData.totalSalesPriceOnSale,
                    summaryData.totalCogsOnSale,
                    vat
                );

                return {
                    id: category.id,
                    categoryName: category.name,
                    ...summaryData,
                    grossMarginPercent,
                };
            });

        const totalSummary = calculateSummary(salesCampaignRows);
        const totalGM = calculateGrossMarginPercent(
            totalSummary.totalSalesPriceOnSale,
            totalSummary.totalCogsOnSale,
            vat
        );

        return [
            ...rows,
            {
                id: 'total',
                categoryName: 'Total',
                ...calculateSummary(salesCampaignRows),
                grossMarginPercent: totalGM,
            },
        ];
    }, [byCategory, styleCategories, salesCampaignRows, vat]);

    return (
        <Container>
            <TableWrapper className="ag-theme-alpine-custom-compact small-font" sx={{ flex: 1 }}>
                <DataTable
                    {...createTotalBoldRowStyle('categoryName')}
                    autoSizeToFit
                    rows={summaryRows}
                    columns={categorySalesSummaryColumns}
                    defaultColDef={defaultColDef}
                />
            </TableWrapper>
        </Container>
    );
};
